<template>
  <teleport to="body">
    <div
      v-if="show"
      class="modal-overlay"
      @click.self="handleClose"
      @keydown.esc="handleClose"
    >
      <div
        class="modal-window"
        ref="seModal"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ce-help-title"
        aria-describedby="ce-help-desc"
      >
        <div class="modal-shell">

          <!-- ============ HEADER ============ -->
          <div class="modal-head">
            <div class="head-icon" aria-hidden="true">
              <i class="bi bi-bullseye"></i>
            </div>
            <div class="head-text">
              <div class="head-eyebrow">{{ $t('gaugeHelp.ce.eyebrow') }}</div>
              <h2 class="head-title" id="ce-help-title">
                {{ $t('gaugeHelp.ce.title') }}
              </h2>
            </div>
            <button
              class="head-close"
              type="button"
              @click="handleClose"
              :aria-label="$t('gaugeHelp.common.close')"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <!-- ============ BODY ============ -->
          <div class="modal-body" id="ce-help-desc">

            <!-- 1. WHAT IT SHOWS -->
            <section class="card-row tldr">
              <div class="card-row-label">{{ $t('gaugeHelp.common.whatItShows') }}</div>
              <i18n-t keypath="gaugeHelp.ce.what" tag="p" class="card-row-body" scope="global">
                <template #best><strong>{{ $t('gaugeHelp.ce.whatBest') }}</strong></template>
                <template #hundred><strong>100%</strong></template>
                <template #peers><strong>{{ $t('gaugeHelp.ce.whatPeers') }}</strong></template>
                <template #bic><strong>{{ $t('gaugeHelp.ce.whatBic') }}</strong></template>
                <template #within><strong>{{ $t('gaugeHelp.ce.whatWithin') }}</strong></template>
                <template #est><em>{{ $t('gauges.row.est') }}</em></template>
              </i18n-t>
            </section>

            <!-- 2. HOW IT'S CALCULATED -->
            <section class="card-row">
              <div class="card-row-label">{{ $t('gaugeHelp.common.howCalculated') }}</div>

              <div class="formula-card">
                <div class="formula-line">
                  <span class="formula-term">{{ $t('gaugeHelp.ce.earned') }}</span>
                  <span class="formula-eq">=</span>
                  <span class="formula-expr">
                    <i18n-t keypath="gaugeHelp.ce.earnedExpr" scope="global">
                      <template #eng><em>{{ $t('gaugeHelp.ce.eng') }}</em></template>
                      <template #buy><em>{{ $t('gaugeHelp.ce.buy') }}</em></template>
                      <template #avg><em>{{ $t('gaugeHelp.ce.avg') }}</em></template>
                    </i18n-t>
                    <span class="formula-note">{{ $t('gaugeHelp.ce.earnedNote') }}</span>
                  </span>
                </div>

                <div class="formula-line">
                  <span class="formula-term">{{ $t('gaugeHelp.ce.best') }}</span>
                  <span class="formula-eq">=</span>
                  <span class="formula-expr">
                    <i18n-t keypath="gaugeHelp.ce.bestExpr" scope="global">
                      <template #sustained><em>{{ $t('gaugeHelp.ce.sustained') }}</em></template>
                    </i18n-t>
                    <span class="formula-note">{{ $t('gaugeHelp.ce.bestNote') }}</span>
                  </span>
                </div>

                <div class="formula-divider"></div>

                <div class="formula-line formula-result">
                  <span class="formula-term">{{ $t('gaugeHelp.ce.result') }}</span>
                  <span class="formula-eq">=</span>
                  <span class="formula-expr">
                    <strong>{{ $t('gaugeHelp.ce.resultExpr') }}</strong>
                  </span>
                </div>
              </div>

              <i18n-t keypath="gaugeHelp.ce.plain" tag="p" class="plain-help" scope="global">
                <template #best><em>{{ $t('gaugeHelp.ce.whatBest') }}</em></template>
                <template #within><strong>{{ $t('gaugeHelp.ce.whatWithin') }}</strong></template>
              </i18n-t>
            </section>

            <!-- 3. YOUR SECTOR'S BENCHMARK -->
            <section class="card-row">
              <div class="card-row-label">{{ $t('gaugeHelp.ce.benchLabel', { sector: sectorInfo.name }) }}</div>

              <div class="bench-card">
                <div class="bench-row">
                  <div class="bench-cell">
                    <div class="bench-val">{{ engagementTargetPct }}%</div>
                    <div class="bench-label">{{ $t('gaugeHelp.ce.targetEng') }}</div>
                    <div class="bench-hint">{{ $t('gaugeHelp.ce.targetEngHint') }}</div>
                  </div>
                  <div class="bench-sep">×</div>
                  <div class="bench-cell">
                    <div class="bench-val">{{ conversionTargetPct }}%</div>
                    <div class="bench-label">{{ $t('gaugeHelp.ce.targetConv') }}</div>
                    <div class="bench-hint">{{ $t('gaugeHelp.ce.targetConvHint') }}</div>
                  </div>
                  <div class="bench-sep">=</div>
                  <div class="bench-cell">
                    <div class="bench-val">{{ combinedPct }}%</div>
                    <div class="bench-label">{{ $t('gaugeHelp.ce.bicRung') }}</div>
                    <div class="bench-hint">{{ $t('gaugeHelp.ce.bicRungHint') }}</div>
                  </div>
                </div>

                <div class="bench-source">
                  <Tooltip :text="confidenceTooltip" placement="right">
                    <span
                      class="confidence-dot"
                      :class="`conf-${sectorInfo.confidenceClass}`"
                      aria-hidden="true"
                    ></span>
                  </Tooltip>
                  <span class="bench-source-label">{{ sectorInfo.confidenceLabel }}:</span>
                  {{ sectorInfo.source }}
                </div>
              </div>
            </section>

            <!-- 4. HOW TO READ YOUR SCORE -->
            <section class="card-row">
              <div class="card-row-label">{{ $t('gaugeHelp.common.howToRead') }}</div>

              <div class="range-card">
                <div class="range-row range-red">
                  <div class="range-band">&lt; 70%</div>
                  <div class="range-body">
                    <strong>{{ $t('gaugeHelp.ce.redTitle') }}</strong>
                    {{ $t('gaugeHelp.ce.redBody') }}
                  </div>
                </div>
                <div class="range-row range-orange">
                  <div class="range-band">70 – 90%</div>
                  <div class="range-body">
                    <strong>{{ $t('gaugeHelp.ce.orangeTitle') }}</strong>
                    {{ $t('gaugeHelp.ce.orangeBody') }}
                  </div>
                </div>
                <div class="range-row range-lightgreen">
                  <div class="range-band">90 – 100%</div>
                  <div class="range-body">
                    <strong>{{ $t('gaugeHelp.ce.lightgreenTitle') }}</strong>
                    {{ $t('gaugeHelp.ce.lightgreenBody') }}
                  </div>
                </div>
                <div class="range-row range-green">
                  <div class="range-band">100% +</div>
                  <div class="range-body">
                    <strong>{{ $t('gaugeHelp.ce.greenTitle') }}</strong>
                    {{ $t('gaugeHelp.ce.greenBody') }}
                  </div>
                </div>
              </div>
            </section>

            <!-- 5. METHODOLOGY (collapsible) -->
            <section class="card-row methodology">
              <button
                class="methodology-toggle"
                type="button"
                @click="methodologyOpen = !methodologyOpen"
                :aria-expanded="methodologyOpen"
              >
                <i
                  class="bi"
                  :class="methodologyOpen ? 'bi-chevron-down' : 'bi-chevron-right'"
                ></i>
                {{ $t('gaugeHelp.ce.methToggle') }}
              </button>

              <div v-if="methodologyOpen" class="methodology-body">
                <p>
                  {{ $t('gaugeHelp.ce.methIntro') }}
                </p>

                <div class="meth-pillar">
                  <span class="confidence-dot conf-pr" aria-hidden="true"></span>
                  <div>
                    <strong>{{ $t('gaugeHelp.common.pillar.pr') }}</strong>
                    <i18n-t keypath="gaugeHelp.ce.methPr" scope="global">
                      <template #cite><em>Hui, Huang, Suher &amp; Inman, 2013, Journal of
                    Marketing Research</em></template>
                    </i18n-t>
                  </div>
                </div>

                <div class="meth-pillar">
                  <span class="confidence-dot conf-cvt" aria-hidden="true"></span>
                  <div>
                    <strong>{{ $t('gaugeHelp.common.pillar.cvt') }}</strong>
                    {{ $t('gaugeHelp.ce.methCvt') }}
                  </div>
                </div>

                <div class="meth-pillar">
                  <span class="confidence-dot conf-srs" aria-hidden="true"></span>
                  <div>
                    <strong>{{ $t('gaugeHelp.common.pillar.srs') }}</strong>
                    {{ $t('gaugeHelp.ce.methSrs') }}
                  </div>
                </div>

                <i18n-t keypath="gaugeHelp.ce.methFine" tag="p" class="meth-fine" scope="global">
                  <template #doc><em>{{ $t('gaugeHelp.common.methodDoc') }}</em></template>
                </i18n-t>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Tooltip from '@/components/ui/Tooltip.vue'
import { CEILINGS_BY_SEGMENT } from '@/prioritization/settings'

const props = defineProps({
  show: { type: Boolean, default: false },
  segmentKey: { type: String, default: 'default' }
})
const emit = defineEmits(['close'])
const { t } = useI18n()

// Per-sector metadata: full name + which methodology pillar drives
// the benchmark for this sector (per the Storalytic Benchmark
// Methodology doc). Confidence dot maps to a colour: PR=green,
// CVT=blue, SRS=amber.
// Name, confidence label and source text are translated (gaugeHelp.sectors.* /
// gaugeHelp.common.conf.* / gaugeHelp.ce.sources.*); the citations stay as written.
const SECTOR_INFO = {
  default:      { confidenceClass: 'srs', confidenceLabelKey: 'srs' },
  grocery:      { confidenceClass: 'pr',  confidenceLabelKey: 'pr' },
  diy:          { confidenceClass: 'srs', confidenceLabelKey: 'srsShort' },
  furniture:    { confidenceClass: 'srs', confidenceLabelKey: 'srsShort' },
  fashion:      { confidenceClass: 'cvt', confidenceLabelKey: 'cvt' },
  discount:     { confidenceClass: 'srs', confidenceLabelKey: 'srsShort' },
  pharmacy:     { confidenceClass: 'srs', confidenceLabelKey: 'srsShort' },
  specialty:    { confidenceClass: 'cvt', confidenceLabelKey: 'cvt' },
  large_format: { confidenceClass: 'cvt', confidenceLabelKey: 'cvt' },
}

const sectorInfo = computed(() => {
  const key = SECTOR_INFO[props.segmentKey] ? props.segmentKey : 'default'
  const info = SECTOR_INFO[key]
  return {
    confidenceClass: info.confidenceClass,
    name: t(`gaugeHelp.sectors.${key}`),
    confidenceLabel: t(`gaugeHelp.common.conf.${info.confidenceLabelKey}`),
    source: t(`gaugeHelp.ce.sources.${key}`),
  }
})

const segCeil = computed(
  () => CEILINGS_BY_SEGMENT[props.segmentKey] || CEILINGS_BY_SEGMENT.default
)

const engagementTargetPct = computed(() => Math.round((segCeil.value.Emax || 0) * 100))
const conversionTargetPct = computed(() => Math.round((segCeil.value.Cmax || 0) * 100))
const combinedPct = computed(() => {
  const raw = (segCeil.value.Emax || 0) * (segCeil.value.Cmax || 0) * 100
  return raw >= 10 ? Math.round(raw) : raw.toFixed(1)
})

const confidenceTooltip = computed(() => {
  const c = sectorInfo.value.confidenceClass
  if (c === 'pr')  return t('gaugeHelp.common.tip.pr')
  if (c === 'cvt') return t('gaugeHelp.common.tip.cvt')
  return t('gaugeHelp.common.tip.srs')
})

const methodologyOpen = ref(false)

const seModal = ref(null)

function handleClose() {
  emit('close')
}

watch(
  () => props.show,
  async (open) => {
    if (open) {
      document.body.classList.add('modal-open')
      await nextTick()
      seModal.value?.focus?.()
    } else {
      document.body.classList.remove('modal-open')
      methodologyOpen.value = false  // reset for next open
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  document.body.classList.remove('modal-open')
})
</script>

<style scoped>
/* ============================================================
   OVERLAY + WINDOW
============================================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13, 26, 38, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100;   /* above the nav sidebar (1100) so the modal isn't covered */
  padding: 24px;
}

.modal-window {
  background: #fff;
  border-radius: 16px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(13, 38, 76, 0.18);
  outline: none;
}

.modal-shell {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

/* ============================================================
   HEADER
============================================================ */
.modal-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 22px;
  border-bottom: 1px solid #eef2f8;
  background: linear-gradient(180deg, #f6f9fd 0%, #fff 100%);
}

.head-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  /* Commercial-accent — blue, matches the gauge card's left border. */
  background: #4F7EB3;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex: 0 0 auto;
}

.head-text {
  flex: 1 1 auto;
  min-width: 0;
}

.head-eyebrow {
  font-size: 11px;
  font-weight: 600;
  color: #6b7a87;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.head-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a2a36;
  margin: 0;
  line-height: 1.25;
}

.head-close {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e6edf3;
  background: #fff;
  color: #6b7a87;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}
.head-close:hover {
  background: #f6f9fd;
  color: #1a2a36;
}

/* ============================================================
   BODY (scrollable area)
   flex: 1 + min-height: 0 lets it shrink-to-fit the remaining
   space below the header AND scroll when content overflows
   (matters when the methodology section is expanded).
============================================================ */
.modal-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 22px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* Header should NOT shrink — body absorbs the variable height. */
.modal-head {
  flex: 0 0 auto;
}

/* ============================================================
   CARD ROW (per-section block)
============================================================ */
.card-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.card-row-label {
  font-size: 11px;
  font-weight: 700;
  color: #4F7EB3;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.card-row-body {
  margin: 0;
  font-size: 14px;
  color: #2b3a46;
  line-height: 1.55;
}

.card-row.tldr .card-row-body {
  font-size: 15px;
}

/* ============================================================
   FORMULA CARD
============================================================ */
.formula-card {
  background: #f6f9fd;
  border: 1px solid #e3ecf7;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.formula-line {
  display: grid;
  grid-template-columns: 110px 18px 1fr;
  gap: 10px;
  align-items: start;
  font-size: 13.5px;
  color: #2b3a46;
  line-height: 1.5;
}

.formula-term {
  font-weight: 700;
  color: #1a2a36;
}

.formula-eq {
  font-weight: 700;
  color: #6b7a87;
  text-align: center;
}

.formula-expr em {
  font-style: italic;
  color: #4F7EB3;
  font-weight: 600;
}

.formula-note {
  display: block;
  font-size: 11.5px;
  color: #7a8895;
  font-style: italic;
  margin-top: 2px;
}

.formula-divider {
  height: 1px;
  background: #d8e3f0;
  margin: 4px 0;
}

.formula-result {
  font-size: 14.5px;
}
.formula-result .formula-expr strong { color: #4F7EB3; }

.plain-help {
  margin: 0;
  font-size: 13px;
  color: #6b7a87;
  line-height: 1.55;
  font-style: italic;
}

/* ============================================================
   BENCHMARK CARD — Commercial blue tone
============================================================ */
.bench-card {
  background: linear-gradient(180deg, #eaf2fa 0%, #f4f8fc 100%);
  border: 1px solid #c8dbef;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.bench-row {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 8px;
}

.bench-cell {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 2px;
}

.bench-val {
  font-size: 28px;
  font-weight: 800;
  color: #4F7EB3;
  letter-spacing: -0.5px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.bench-label {
  font-size: 12px;
  font-weight: 600;
  color: #2c5077;
  margin-top: 4px;
}

.bench-hint {
  font-size: 11px;
  color: #6b7a87;
  font-style: italic;
  line-height: 1.3;
  max-width: 130px;
}

.bench-sep {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  color: #9fbfdc;
  flex: 0 0 auto;
}

.bench-source {
  font-size: 12px;
  color: #2c5077;
  display: flex;
  gap: 6px;
  align-items: baseline;
  padding-top: 10px;
  border-top: 1px solid #c8dbef;
  line-height: 1.5;
}

.bench-source-label {
  font-weight: 700;
}

/* ============================================================
   CONFIDENCE DOT
============================================================ */
.confidence-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex: 0 0 auto;
  position: relative;
  top: 1px;
}
.conf-pr  { background: #36910d; }
.conf-cvt { background: #4F7EB3; }
.conf-srs { background: #D3A741; }

/* ============================================================
   RANGE CARD
============================================================ */
.range-card {
  background: #fff;
  border: 1px solid #e3ecf7;
  border-radius: 12px;
  overflow: hidden;
}

.range-row {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 12px;
  align-items: center;
  padding: 12px 16px;
  font-size: 13.5px;
  color: #2b3a46;
  line-height: 1.5;
  border-left: 4px solid transparent;
  border-bottom: 1px solid #eef2f8;
}
.range-row:last-child { border-bottom: none; }

.range-band {
  font-weight: 700;
  color: #1a2a36;
  font-variant-numeric: tabular-nums;
}

.range-body strong { color: #1a2a36; }

.range-red        { border-left-color: #B44A55; background: #fdf5f5; }
.range-orange     { border-left-color: #E9A44A; background: #fefaf2; }
.range-lightgreen { border-left-color: #8DC468; background: #f8fcf3; }
.range-green      { border-left-color: #36910d; background: #f4faec; }

/* ============================================================
   METHODOLOGY (collapsible)
============================================================ */
.methodology {
  margin-top: 6px;
}

.methodology-toggle {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #4F7EB3;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  text-align: left;
}
.methodology-toggle:hover { text-decoration: underline; }
.methodology-toggle .bi { font-size: 12px; }

.methodology-body {
  margin-top: 12px;
  padding: 16px 18px;
  background: #f6f9fd;
  border: 1px solid #e3ecf7;
  border-radius: 12px;
  font-size: 13px;
  color: #2b3a46;
  line-height: 1.55;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.methodology-body p { margin: 0; }
.methodology-body strong { color: #1a2a36; }

.meth-pillar {
  display: grid;
  grid-template-columns: 14px 1fr;
  gap: 8px;
  align-items: start;
  font-size: 12.5px;
}

.meth-fine {
  font-size: 12px;
  color: #6b7a87;
  font-style: italic;
  border-top: 1px solid #d8e3f0;
  padding-top: 10px;
  margin-top: 4px;
}

/* ============================================================
   GLOBAL: lock body scroll while modal is open
============================================================ */
:global(body.modal-open) {
  overflow: hidden;
}

/* ============================================================
   RESPONSIVE
============================================================ */
@media (max-width: 540px) {
  .bench-row {
    flex-direction: column;
    gap: 14px;
  }
  .bench-sep {
    transform: rotate(90deg);
  }
  .formula-line {
    grid-template-columns: 1fr;
    gap: 4px;
  }
  .formula-eq { display: none; }
  .range-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>
